import { useCallback, useEffect, useState } from 'react';

/**
 * State that survives a reload, for per-viewer preferences.
 *
 * For the small choices someone makes about how they want to look at a screen
 * — which view, which sort, which filter — and nothing else. It is per-BROWSER
 * and never reaches the server, so it must not hold anything another device or
 * another session needs to see, and nothing that matters if it vanishes.
 *
 * Every access is wrapped. `localStorage` is not merely empty in a private
 * window or with site data blocked — the accessor itself THROWS, and an
 * uncaught throw here takes the whole page down with it. So a read that fails
 * yields the fallback, a write that fails is dropped, and the component
 * renders correctly either way.
 *
 * `allow` guards the value on the way back in. A stale key from an older build
 * is not a hypothetical — this is exactly where a renamed sort column would
 * come back as a string nothing knows how to apply, and `undefined` sort order
 * is a blank screen rather than an error anyone can act on.
 */
export function usePersistentState(key, fallback, allow) {
  const permitted = useCallback(
    (value) => {
      if (value === null || value === undefined) return false;
      if (typeof allow === 'function') return allow(value);
      if (Array.isArray(allow)) return allow.includes(value);
      return true;
    },
    [allow]
  );

  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      // Stored as JSON so a non-string preference (an object of column widths,
      // say) works without every caller inventing its own encoding.
      const parsed = JSON.parse(raw);
      return permitted(parsed) ? parsed : fallback;
    } catch (e) {
      return fallback;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode, blocked site data, quota — the preference is a nicety */
    }
  }, [key, value]);

  return [value, setValue];
}

export default usePersistentState;
